import { useCallback, useMemo, useState } from 'react';
import _ from 'lodash';
import {
    buildUpdatePayload,
    createInitialUpdatedValues,
    getFormData,
    hasCompanyChanges
} from '../utils/companyDetailsUtils';

/**
 * Maintains editable company details while retaining the existing update semantics.
 *
 * @param {object|null} company
 */
export const useCompanyDetailsForm = (company = null) => {
    const [updatedValues, setUpdatedValues] = useState(() => createInitialUpdatedValues(company));

    const onChangeProperty = useCallback(property => value => {
        setUpdatedValues(previousValues => ({
            ...previousValues,
            [property]: value
        }));
    }, []);

    const getFieldValue = useCallback(field => {
        if (!company) return undefined;

        if (field.sourceField) {
            const source = updatedValues[field.sourceField] || company[field.sourceField] || {};
            return source[field.fieldName];
        }

        return _.has(updatedValues, field.fieldName) ? updatedValues[field.fieldName] : company[field.fieldName];
    }, [company, updatedValues]);

    const formData = useMemo(() => getFormData(company || {}, updatedValues), [company, updatedValues]);
    const hasChanges = useCallback(fieldNames => (
        hasCompanyChanges(company, updatedValues, fieldNames)
    ), [company, updatedValues]);
    const getUpdatePayload = useCallback(fieldNames => (
        buildUpdatePayload(company || {}, updatedValues, fieldNames)
    ), [company, updatedValues]);
    const resetFields = useCallback(fieldNames => {
        setUpdatedValues(previousValues => _.omit(previousValues, fieldNames));
    }, []);

    return {
        formData,
        getFieldValue,
        getUpdatePayload,
        hasChanges,
        onChangeProperty,
        resetFields
    };
};
